import Icon from '../ui/Icon';

/**
 * Shown when a shared link named products or answers that could not be applied.
 *
 * WHY THIS EXISTS: previously an unresolvable share parameter produced a console.warn and
 * nothing else. The prospect saw a quote quietly missing items, and the account manager who
 * sent the link had no idea it had gone stale -- the failure was invisible to precisely the two
 * people who needed to know. Products get renamed and retired; links live in email threads for
 * years, so this is a "when", not an "if".
 *
 * role="status" rather than "alert": it is informational and arrives at page load, so it should
 * not interrupt a screen reader mid-sentence.
 */
export default function StaleLinkBanner({ items, onDismiss }) {
  if (!items || items.length === 0) return null;

  return (
    <div
      className="border-b border-gray-200 bg-warning-50 px-4 py-3.5 sm:px-7"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <Icon name="warningTriangle" className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning-600" />
        <div className="min-w-0 flex-1 text-sm text-warning-800">
          <p className="font-medium">Some items from this link could not be applied</p>
          <p className="mt-1">
            These are no longer available, or are not offered with the selected plan and billing
            period:
          </p>
          <ul className="mt-1 list-inside list-disc">
            {items.map((item) => (
              <li key={item} className="break-words">
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-2">
            Please review your selections below, or ask whoever sent you this link for an updated
            one.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          // 44px minimum tap target. The resume banner's dismiss X was under that, which the
          // audit flagged -- no reason to repeat it here.
          className="-m-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded text-warning-600 hover:text-warning-800 focus:outline-none focus:ring-2 focus:ring-warning-500"
          aria-label="Dismiss this notice"
        >
          <Icon name="close" className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
